import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormTextField,
  FormSelect,
  EnumValueDisplay,
  NameDisplay,
  FormCheckbox,
  useHasRole,
  useObservable,
} from '@lib/common';
import { Admin, Manager, Properties, Reservation } from '@lib/state';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { TicketStatus } from '../../state/maintenance/maintenance-ticket.model';
import {
  MaintenanceCategory,
  MaintenanceCategoryChild,
} from '../../state/maintenance/maintenance-ticket.model';
import { MaintenanceTicketFormFields } from './maintenance-ticket-modal';
import { ReservationMaintenanceDetails } from 'app/reservations/components/reservation-maintenance-details';
import { maintenanceTicketQuery, maintenanceTicketService } from 'app/state';
import { MaintenanceTicketDuplicateDisplay } from './maintenance-ticket-duplicate-display';

const CLOSED_OR_COMPLETED: Properties.MaintenanceTicketStatusEnum[] = [
  Properties.MaintenanceTicketStatusEnum.Completed,
  Properties.MaintenanceTicketStatusEnum.Closed,
];

interface Props {
  rooms: Properties.RoomModel[];
  employees: Properties.EmployeeModel[];
  ticket?: Properties.MaintenanceTicketModel;
  hasManagerRole: boolean;
  hasMaintenanceRole: boolean;
  setSelectedRoomId: (roomId?: string) => void;
  setSelectedEmployeeId?: (employeeId?: string) => void;
  selectedRoomId?: string;
  setArea: (area?: string) => void;
  area?: string;
  selectedReservation?: Reservation;
  categories: MaintenanceCategory[];
  propertyId: string;
}

export const MaintenanceTicketForm: React.FC<Props> = ({
  ticket,
  rooms,
  employees,
  hasManagerRole,
  hasMaintenanceRole,
  setSelectedRoomId,
  setSelectedEmployeeId,
  selectedRoomId,
  setArea,
  area,
  selectedReservation,
  categories,
  propertyId,
}) => {
  const canUpdateActive = useHasRole(Admin, Manager);
  const activeTickets = useObservable(maintenanceTicketQuery.possibleDuplicates);
  const dupTicketPagination = useObservable(maintenanceTicketQuery.duplicateTicketPaginationState);
  const { watch, setValue } = useFormContext<MaintenanceTicketFormFields>();
  const formStatus = watch('status');
  const formCategoryId = watch('categoryId');
  const room = watch('roomId');

  const location = useMemo(
    () => (!!room && room !== '' ? rooms.find(r => r.id === room)?.roomNumber : area),
    [room, rooms, area]
  );

  // parentCategoryId is UI-only (never submitted). It lives in local state rather than
  // react-hook-form because RHF v5's setValue() does not reliably re-render Controller-based
  // FormSelect components or watch() consumers — see bulk-change-billing-initiate-dialog.tsx
  // for the documented precedent. Local state guarantees the parent select refreshes when the
  // auto-sync effect below backfills it from an existing ticket categoryId.
  // Sentinel semantics: `undefined` = not yet derived (auto-sync may backfill); `null` = the user
  // explicitly chose "None" (auto-sync must NOT re-derive); a number = a header is selected.
  const [formParentId, setFormParentId] = useState<number | null | undefined>(undefined);

  const resolutionVisible = CLOSED_OR_COMPLETED.includes(formStatus);
  const selectedCategoryIsOther =
    categories
      .flatMap((h: MaintenanceCategory) => h.children ?? [])
      .find((c: MaintenanceCategoryChild) => c.categoryId === formCategoryId)?.isOther === true;
  // Gate on `formParentId !== null` so choosing "None" (formParentId === null) hides the Other
  // textbox even though watch('categoryId') can lag (RHF v5 Controller setValue quirk). `undefined`
  // (not-yet-derived, e.g. modal open on an Other ticket) still passes so pre-population shows.
  const userCategoryVisible = selectedCategoryIsOther && formParentId !== null;

  const selectedParent = categories.find((h: MaintenanceCategory) => h.categoryId === formParentId);
  const childOptions: MaintenanceCategoryChild[] = selectedParent?.children ?? [];

  // Clear-on-switch-away for userCategory. Keyed on `userCategoryVisible` so it also fires when the
  // user picks "None" (formParentId === null) even though watch('categoryId') lags. Gate on
  // `categories.length > 0` because on modal open for an existing Other-category ticket, `categories`
  // starts empty (async load) which makes the field transiently non-visible and would wipe the
  // pre-populated value before the picker data arrives.
  useEffect(() => {
    if (categories.length === 0) return;
    if (!userCategoryVisible) {
      setValue('userCategory', '');
    }
  }, [categories, userCategoryVisible, setValue]);

  // Auto-sync: on mount / edit reveal, derive parentCategoryId from a pre-existing categoryId.
  // Guard on `!== undefined` (not `!= null`) so an explicit "None" selection (formParentId === null)
  // is NOT re-derived from a stale watched categoryId — RHF v5 setValue() does not reliably update
  // watch('categoryId') for the Controller-based child FormSelect, so formCategoryId can lag here.
  useEffect(() => {
    if (categories.length === 0) return;
    if (formCategoryId == null) return;
    if (formParentId !== undefined) return;
    const owner = categories.find((h: MaintenanceCategory) =>
      (h.children ?? []).some((c: MaintenanceCategoryChild) => c.categoryId === formCategoryId)
    );
    if (owner?.categoryId != null) {
      setFormParentId(owner.categoryId);
    }
  }, [categories, formCategoryId, formParentId]);

  // Auto-clear: when parent changes, drop a child selection that no longer belongs to it.
  // Note: RHF v5 setValue may not visually refresh the child <FormSelect>, but MUI will render
  // the stale value as blank (no matching MenuItem), which naturally prompts the user to re-pick.
  useEffect(() => {
    if (formParentId == null) return;
    const owner = categories.find((h: MaintenanceCategory) => h.categoryId === formParentId);
    const stillValid = (owner?.children ?? []).some(
      (c: MaintenanceCategoryChild) => c.categoryId === formCategoryId
    );
    if (!stillValid) {
      setValue('categoryId', null);
      setValue('userCategory', '');
    }
  }, [formParentId, categories, formCategoryId, setValue]);

  useEffect(() => {
    return () => {
      maintenanceTicketService.resetDuplicateTickets();
    };
  }, []);

  useEffect(() => {
    if (!ticket?.id && propertyId !== '')
      maintenanceTicketService.getDuplicateMainenanceTickets(propertyId, location);
  }, [location, rooms, ticket, propertyId]);

  const onLoadMoreDups = useCallback(() => {
    if (
      dupTicketPagination &&
      dupTicketPagination.continuationToken &&
      !dupTicketPagination.isDone &&
      !ticket?.id &&
      propertyId !== ''
    ) {
      maintenanceTicketService.getDuplicateMainenanceTickets(
        propertyId,
        location,
        dupTicketPagination.continuationToken
      );
    }
  }, [location, ticket, propertyId, dupTicketPagination]);

  const onRoomChange = useCallback(
    (e: EventTarget) => {
      setSelectedRoomId((e as HTMLInputElement).value);
    },
    [setSelectedRoomId]
  );
  const onEmployeeChange = useCallback(
    (e: EventTarget) => {
      if (setSelectedEmployeeId) setSelectedEmployeeId((e as HTMLInputElement).value);
    },
    [setSelectedEmployeeId]
  );
  const onAreaChange = useCallback(
    (e: EventTarget) => {
      setArea((e as HTMLInputElement).value);
    },
    [setArea]
  );

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormSelect
            name="roomId"
            label="Room"
            fullWidth
            required={!area}
            onClick={e => {
              onRoomChange(e.target);
            }}
          >
            <MenuItem value={undefined}>None</MenuItem>
            {rooms.map(m => (
              <MenuItem key={m.id} value={m.id}>
                {m.roomNumber}
              </MenuItem>
            ))}
          </FormSelect>
        </Grid>
        <Grid item xs={6}>
          <FormTextField
            name="area"
            label="Area"
            fullWidth
            multiline={true}
            required={!selectedRoomId}
            onChange={e => {
              onAreaChange(e.target);
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {!!selectedRoomId && (
            <ReservationMaintenanceDetails
              reservation={selectedReservation}
            ></ReservationMaintenanceDetails>
          )}
        </Grid>
        {ticket?.id && (
          <Grid item xs={12} md={6}>
            <FormSelect name="status" label="Status" fullWidth required>
              {Object.values(Properties.MaintenanceTicketStatusEnum).map(x => (
                <MenuItem
                  key={x}
                  value={x}
                  dense
                  disabled={x === TicketStatus.Closed && !hasManagerRole}
                >
                  <EnumValueDisplay value={x} disableTypography />
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <FormSelect name="priority" label="Priority" fullWidth required>
            {Object.values(Properties.PriorityOptions).map(x => (
              <MenuItem key={x} value={x} dense>
                <EnumValueDisplay value={x} disableTypography />
              </MenuItem>
            ))}
          </FormSelect>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormSelect
            name="assignedTo"
            label="Assigned To"
            fullWidth
            onClick={e => {
              onEmployeeChange(e.target);
            }}
            disabled={!(hasManagerRole || hasMaintenanceRole)}
          >
            <MenuItem value={''}>Unassigned</MenuItem>
            {employees.map(m => (
              <MenuItem key={m.userId} value={m.userId}>
                {<NameDisplay {...m.name} directory disableTypography />}
              </MenuItem>
            ))}
          </FormSelect>
        </Grid>
        {ticket?.id && (
          <Grid container item xs={12} md={6} justifyContent="flex-end">
            <Grid item xs={1}>
              <FormCheckbox
                name="isActive"
                label="Active"
                labelPlacement="top"
                defaultChecked={ticket?.isActive}
                disabled={canUpdateActive === false}
              />
            </Grid>
          </Grid>
        )}
        {!ticket?.id && (
          <Grid item xs={12}>
            <FormTextField
              name="description"
              label="Note"
              fullWidth
              minRows={3}
              multiline={true}
              required
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Category"
            fullWidth
            value={formParentId ?? ''}
            onChange={e => {
              const raw = e.target.value;
              // `null` (not `undefined`) marks an explicit "None" so the auto-sync effect won't
              // re-derive the parent from a stale watched categoryId (see the effect's guard).
              const next = raw === '' ? null : Number(raw);
              setFormParentId(next);
              // Selecting "None" hides the child picker; clear any lingering sub-category id so a
              // stale value isn't submitted (the auto-clear effect early-returns when parent is null).
              if (next == null) {
                setValue('categoryId', null);
              }
            }}
          >
            <MenuItem value={''}>None</MenuItem>
            {categories.map((header: MaintenanceCategory) => (
              <MenuItem key={header.categoryId} value={header.categoryId}>
                {header.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {!ticket?.id &&
          activeTickets &&
          activeTickets.length > 0 &&
          activeTickets.every(
            t =>
              t.roomNumber === location ||
              t.area?.toLowerCase().includes(location?.trim().toLowerCase() ?? '')
          ) && (
            <MaintenanceTicketDuplicateDisplay
              tickets={activeTickets}
              loadMore={onLoadMoreDups}
              // Assume is done if pagination doesn't exist
              isDone={dupTicketPagination?.isDone ?? true}
            />
          )}
        {formParentId != null && (
          <Grid item xs={12} md={6}>
            <FormSelect
              name="categoryId"
              label="Sub-category"
              fullWidth
              required={formParentId != null}
              validationOptions={
                formParentId != null ? { required: 'Sub-category is required' } : {}
              }
            >
              {childOptions.map((child: MaintenanceCategoryChild) => (
                <MenuItem key={child.categoryId} value={child.categoryId}>
                  {child.name}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
        )}
        {userCategoryVisible && (
          <Grid item xs={12} md={12}>
            <FormTextField
              name="userCategory"
              label="Other — please specify"
              fullWidth
              inputProps={{ maxLength: 100 }}
              required={userCategoryVisible}
              validationOptions={
                userCategoryVisible
                  ? {
                      required: 'Please specify the category',
                      validate: value =>
                        (value?.trim().length ?? 0) > 0 || 'Please specify the category',
                    }
                  : {}
              }
            />
          </Grid>
        )}
        {ticket?.id && (
          <Grid item xs={12} md={12}>
            <FormTextField
              name="resolutionDescription"
              label="Resolution Description"
              fullWidth
              multiline
              minRows={3}
              inputProps={{ maxLength: 2000 }}
              required={resolutionVisible}
              validationOptions={
                resolutionVisible
                  ? {
                      required: 'Resolution Description is required',
                      validate: value =>
                        (value?.trim().length ?? 0) > 0 || 'Resolution Description is required',
                    }
                  : {}
              }
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
